import {WebGl2ImageCacheBase} from "#webgl2/webgl2-image-cache-base"
import {WebGl2ImageVirtual} from "#webgl2/webgl2-image-virtual"
import type {HalImage, HalImageDescriptor, HalImageVirtual} from "@cm/hal"
import {getNumChannels} from "@cm/hal"
import {isHalImageVirtual} from "@cm/hal/hal-image/utils"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"

export class WebGl2ImageCacheVirtual extends WebGl2ImageCacheBase {
    protected cacheName = "virtual"

    private _atlases: WebGl2ImageAtlas[] = []
    private _numAtlasPages: number
    private _maxNumAtlases: number

    constructor(halContext: WebGl2Context, sizeInBytes: number, numAtlasPages: number, maxNumAtlases: number) {
        super(halContext, sizeInBytes, false)
        this._numAtlasPages = numAtlasPages
        this._maxNumAtlases = maxNumAtlases
        // Create the first atlas eagerly
        this._atlases.push(new WebGl2ImageAtlas(halContext, {id: 0, numPages: numAtlasPages}))
    }

    get atlases(): WebGl2ImageAtlas[] {
        return this._atlases
    }

    get atlasImageDescriptor() {
        return this._atlases[0].image.descriptor
    }

    getAtlasById(id: number): WebGl2ImageAtlas {
        const atlas = this._atlases[id]
        if (!atlas) {
            throw new Error(`Atlas with id ${id} not found`)
        }
        return atlas
    }

    findAtlasForAllocation(count: number): WebGl2ImageAtlas {
        // Pass 1: existing atlas with enough free (unused) pages — no eviction needed.
        for (const atlas of this._atlases) {
            if (atlas.stats.numPages - atlas.stats.numUsedPages >= count) {
                return atlas
            }
        }
        // Pass 2: expand capacity — a fresh atlas always has free pages.
        if (this._atlases.length < this._maxNumAtlases) {
            const newAtlas = new WebGl2ImageAtlas(this.halContext, {id: this._atlases.length, numPages: this._numAtlasPages})
            this._atlases.push(newAtlas)
            return newAtlas
        }
        // Pass 3: evict from the atlas with the most stale unlocked pages.
        // Require >= count unlocked pages so the lock loop cannot exhaust the queue.
        // If no atlas qualifies, fall back to the one with the most unlocked pages.
        const qualified = this._atlases.filter((a) => a.numAvailablePages >= count)
        const candidates = qualified.length > 0 ? qualified : this._atlases
        return candidates.reduce((best, a) => (a.leastRecentlyUsedTime < best.leastRecentlyUsedTime ? a : best))
    }

    disposeAtlases(): void {
        for (const atlas of this._atlases) {
            atlas.dispose()
        }
        this._atlases = []
    }

    protected estimateImageSizeInBytes(descriptor: HalImageDescriptor): number {
        const {width, height, channelLayout} = descriptor
        const numPages = {
            x: Math.ceil(width / this.halContext.descriptor.atlasPageSize),
            y: Math.ceil(height / this.halContext.descriptor.atlasPageSize),
        }
        const numChannels = getNumChannels(channelLayout)
        const bytesPerChannel = 4
        return numPages.x * numPages.y * numChannels * bytesPerChannel
    }

    protected allocImage(descriptor: HalImageDescriptor): HalImageVirtual {
        return new WebGl2ImageVirtual(this.halContext, descriptor)
    }

    protected override releaseImage(image: HalImage): void {
        if (!isHalImageVirtual(image)) {
            throw new Error("WebGl2ImageCacheVirtual can only manage HalImageVirtual")
        }
        image.freeAllPages()
        super.releaseImage(image)
    }
}
